import React from 'react';
import Chart from 'chart.js';
import { ChartComponent } from '../../general/ChartComponent';
import { ForecastValue } from '../../../api/robotapi';
import { merge } from 'lodash';
import { toPrice } from '../../../utils/parsers';
import ChartDataLabels, { Context } from 'chartjs-plugin-datalabels';
import { COLORS } from './../../../config/constants';

Chart.defaults.global.defaultFontFamily = 'museo-sans, sans-serif';
Chart.defaults.global.defaultFontColor = COLORS.bndGrey;
Chart.defaults.global.defaultFontSize = 16;

// extract ChartDataSet interface from chartjs-plugin-datalabels, which merges extra options
type ChartDataSet = Context['dataset'];

export type ForecastChartProperties = {
    values: ForecastValue[];
    type: 'line' | 'bar';
    goal: number;
    inYears: number;
    deposit: number;
};

export const ForecastChartMobile: React.FC<ForecastChartProperties> = ({
    values,
    type = 'bar',
    inYears,
    goal,
    deposit,
}) => {
    const datasetConfig = { data: { datasets: toDataSets(values, type, deposit, goal) } };

    // merge config
    let config: Chart['config'] = merge({}, chartConfig, datasetConfig);

    return (
        <>
            <div className="flex text-sm font-medium text-bnd-white-1 justify-between ">
                <p className=" bg-bnd-pink px-2 rounded-lg">
                    Inleg: {toPrice(deposit, { onlyFullEuros: true, withCurrencySymbol: true })}
                </p>
                <p className=" bg-bnd-green px-2 rounded-lg" hidden={goal <= 0}>
                    Doel: {toPrice(goal, { onlyFullEuros: true, withCurrencySymbol: true })}
                </p>
            </div>
            <div className="w-full mt-2" style={{ minHeight: '400px' }}>
                <ChartComponent config={config} />
            </div>
        </>
    );
};

/** Parses ForecastValues to Chartjs Datasets */
const toDataSets = (
    forecastValues: ForecastValue[],
    forecastType: ForecastChartProperties['type'],
    initialDeposit: number,
    goal: number,
): ChartDataSet[] => {
    if (forecastType !== 'bar') return [];
    if (forecastValues.length === 0) return [];

    const { pessimistic, optimistic, median } = forecastValues[forecastValues.length - 1];
    const goalIsZero = goal === 0;
    return [
        {
            type: 'bar',
            data: [0, pessimistic, median, optimistic, 0],
            backgroundColor: [COLORS.bndBlue, COLORS.bndBlue, COLORS.bndBlue, COLORS.bndBlue, COLORS.bndBlue],
            borderWidth: 0,
            maxBarThickness: 85,
            datalabels: {
                align: 'end',
                anchor: 'end',
                color: COLORS.bndBlack,
                font: function (context) {
                    return {
                        size: 16,
                        weight: 'bold',
                    };
                },
                formatter: function (value, context) {
                    return toPrice(value, { onlyFullEuros: true, withCurrencySymbol: true });
                },
                display: function (context) {
                    const data = context.chart.data.datasets?.[context.datasetIndex].data;
                    if (context.dataIndex === 0 || (data && context.dataIndex === data.length - 1)) {
                        return false;
                    }
                    return true;
                },
            },
        },
        {
            type: 'line',
            data: [initialDeposit, initialDeposit, initialDeposit, initialDeposit, initialDeposit],
            fill: true,
            backgroundColor: COLORS.bndPinkLight,
            borderColor: COLORS.bndPink,
            borderWidth: 2,
            borderDash: [8, 12],
            pointHitRadius: 0,
            pointRadius: 0,
            hoverRadius: 0,
            hoverBorderWidth: 0,
            hitRadius: 0,
            datalabels: {
                display: false,
            },
        },
        {
            type: 'line',
            data: [goal, goal, goal, goal, goal],
            lineTension: 0,
            fill: false,
            borderColor: COLORS.bndGreen,
            borderWidth: 2,
            borderDash: [8, 12],
            pointHitRadius: 0,
            pointRadius: 0,
            hoverRadius: 0,
            hoverBorderWidth: 0,
            hitRadius: 0,
            showLine: !goalIsZero,
            datalabels: {
                display: false,
            },
        },
    ];
};

const chartConfig: Chart['config'] = {
    type: 'line',
    data: {
        labels: ['', 'Slechte', 'Verwachte', 'Goede', ''],
        datasets: [{ data: [0, 0, 0, 0, 0] }],
    },
    plugins: [ChartDataLabels],
    options: {
        tooltips: {
            callbacks: {
                label: function (tooltipItem, data) {
                    if (tooltipItem.datasetIndex !== undefined && tooltipItem.index !== undefined) {
                        let value: number = (data?.datasets?.[tooltipItem.datasetIndex]?.data?.[tooltipItem.index] ||
                            0) as number;
                        return `${toPrice(value, { onlyFullEuros: true, withCurrencySymbol: true })}`;
                    }
                    return '';
                },
            },
        },
        layout: {
            padding: {
                top: 24,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        legend: {
            display: false,
        },
        scales: {
            yAxes: [
                {
                    position: 'right',
                    gridLines: {
                        color: 'rgba(36, 36, 36, 0.6)',
                        lineWidth: 1.2,
                        drawBorder: false,
                        drawTicks: false,
                    },
                    ticks: {
                        display: false,
                        padding: 8,
                        beginAtZero: true,
                        callback: function (value, index, values) {
                            return toPrice(+value);
                        },
                    },
                },
            ],
            xAxes: [
                {
                    stacked: true,
                    gridLines: {
                        display: false,
                    },
                },
            ],
        },
    },
};
